import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FiHome, FiMessageSquare, FiClipboard, FiUsers,
  FiAlertTriangle, FiShield, FiFileText,
  FiCreditCard, FiSettings, FiChevronLeft, FiChevronRight,
  FiChevronDown, FiZap, FiBookOpen, FiSunrise,
  FiGitBranch, FiTrello, FiDollarSign, FiTrendingUp,
  FiArchive, FiCamera, FiShare2,
  FiBriefcase, FiCalendar, FiCheckSquare,
  FiVideo, FiPlayCircle, FiLayers, FiGlobe, FiGrid,
} from 'react-icons/fi'
import { IoPartlySunnyOutline } from 'react-icons/io5'
import Logo from '../brand/Logo'
import { cn } from '../../lib/utils'
import { useAuthStore } from '../../store/authStore'
import ProgressBar from '../ui/ProgressBar'
import { useUsage } from '../../hooks/useUsage'
import { useFeatureNav, ROUTE_FEATURE_MAP } from '../../hooks/useFeatureNav'

// ──────────────────────────────────────────────
// 메뉴 정의
// ──────────────────────────────────────────────
interface NavItem {
  to?: string
  labelKey: string
  icon: React.ReactNode
  // NavLink 정확 매칭 — 다른 자식 경로의 prefix 가 되는 경우 active 중복 방지.
  end?: boolean
  // 자식 메뉴가 있으면 to 는 사용 X — 펼침/접기 토글로 동작.
  children?: NavItem[]
}

interface NavGroup {
  labelKey: string
  icon: React.ReactNode
  items: NavItem[]
  collapsible?: boolean
}

const navGroups: NavGroup[] = [
  {
    labelKey: 'nav.aiFeatures',
    icon: <FiZap size={16} />,
    items: [
      // { to: '/dashboard/chat-studio', labelKey: 'nav.chatStudio', icon: <FiCpu size={16} /> }, // 보류 — 라우트는 유지, 사이드바만 숨김
      { to: '/dashboard/daily-brief', labelKey: 'nav.dailyBrief', icon: <FiSunrise size={16} /> },
      { to: '/dashboard/work-timing', labelKey: 'nav.workTiming', icon: <IoPartlySunnyOutline size={16} /> },
      // { to: '/dashboard/integrated', labelKey: 'nav.integrated', icon: <FiZap size={16} /> }, // 보류 — 통합 생성은 사업장 허브에서만 진행. 라우트는 유지, 사이드바만 숨김

      { to: '/dashboard/chatbot', labelKey: 'nav.chatbot', icon: <FiMessageSquare size={16} /> },
      // 안전교육 영상 — 법정 교육 영상 + 강의실 서브메뉴
      {
        labelKey: 'nav.safetyEducation',
        icon: <FiBookOpen size={16} />,
        children: [
          // mode=single → 단편 영상(기본 모드). mode=course → 법정 교육 대체 토글 ON (SafetyEducation lazy init).
          { to: '/dashboard/safety-education?mode=single', labelKey: 'nav.safetyEducationSingle', icon: <FiVideo size={16} />, end: true },
          { to: '/dashboard/safety-education?mode=course', labelKey: 'nav.safetyEducationLegal', icon: <FiLayers size={16} />, end: true },
          { to: '/dashboard/safety-education/library', labelKey: 'nav.safetyEducationLibrary', icon: <FiPlayCircle size={16} /> },
        ],
      },
      // 위험성평가 — JSA + 위험성평가표 서브메뉴
      {
        labelKey: 'nav.riskAssessmentGroup',
        icon: <FiShield size={16} />,
        children: [
          { to: '/dashboard/jsa', labelKey: 'nav.jsaShort', icon: <FiClipboard size={16} /> },
          { to: '/dashboard/risk-assessment', labelKey: 'nav.riskAssessmentTable', icon: <FiShield size={16} /> },
        ],
      },
      { to: '/dashboard/work-permit', labelKey: 'nav.workPermit', icon: <FiFileText size={16} /> },
      { to: '/dashboard/tbm', labelKey: 'nav.tbm', icon: <FiUsers size={16} /> },
      // image-analysis 라우트는 유지(호환), 사이드바에서만 숨김 — corrective(현장 분석) 가 단일 진입점
      { to: '/dashboard/corrective', labelKey: 'nav.siteAnalysis', icon: <FiCamera size={16} /> },
      { to: '/dashboard/accident-report', labelKey: 'nav.incidentInvestigation', icon: <FiAlertTriangle size={16} /> },
      // 법규 준수 점검 — 점검(매칭) + 이행 대장 서브메뉴
      {
        labelKey: 'nav.complianceCheck',
        icon: <FiCheckSquare size={16} />,
        children: [
          { to: '/dashboard/safety-rule-match', labelKey: 'nav.complianceCheckRun', icon: <FiCheckSquare size={16} />, end: true },
          { to: '/dashboard/safety-rule-match/ledger', labelKey: 'nav.complianceLedger', icon: <FiBriefcase size={16} /> },
        ],
      },
    ],
    collapsible: true,
  },
  {
    labelKey: 'nav.advancedAnalysis',
    icon: <FiTrendingUp size={16} />,
    items: [
      { to: '/dashboard/risk-analysis', labelKey: 'nav.riskAnalysis', icon: <FiGitBranch size={16} /> },
      { to: '/dashboard/safety-cost', labelKey: 'nav.safetyCost', icon: <FiDollarSign size={16} /> },
      { to: '/dashboard/equipment-cert', labelKey: 'nav.equipmentCert', icon: <FiShield size={16} /> },
      { to: '/dashboard/safety-signs', labelKey: 'nav.safetySigns', icon: <FiGlobe size={16} /> },
      { to: '/dashboard/pnid', labelKey: 'nav.pnid', icon: <FiShare2 size={16} /> },
    ],
    collapsible: true,
  },
  {
    labelKey: 'nav.records',
    icon: <FiArchive size={16} />,
    items: [
      { to: '/dashboard/records', labelKey: 'nav.myRecords', icon: <FiArchive size={16} /> },
    ],
    collapsible: true,
  },
]

const PLAN_LABEL_KEYS: Record<string, string> = {
  free: 'common.plan.free',
  starter: 'common.plan.starter',
  pro: 'common.plan.pro',
  enterprise: 'common.plan.enterprise',
}

// 배경 색상이 있는 모든 곳은 중성 진한 텍스트 (라이트=zinc-900, 다크=white).
// 색은 배경/보더로만 차별화 — text 자체는 항상 고대비.
const planBadgeStyle: Record<string, string> = {
  free:       'bg-zinc-200 text-zinc-900 dark:bg-zinc-800 dark:text-white',
  starter:    'bg-amber-100 text-zinc-900 border border-amber-400 dark:bg-amber-500/15 dark:text-white dark:border-amber-500/40',
  pro:        'bg-amber-200 text-zinc-900 border border-amber-500 font-semibold dark:bg-amber-500/25 dark:text-white dark:border-amber-500/50',
  enterprise: 'bg-amber-300 text-zinc-900 border border-amber-600 font-semibold dark:bg-amber-500/30 dark:text-white dark:border-amber-500/60',
}

// ──────────────────────────────────────────────
interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  mobileOpen?: boolean
  onMobileClose?: () => void
}

export default function Sidebar({ collapsed: collapsedProp, onToggle, mobileOpen, onMobileClose }: SidebarProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { data: usage } = useUsage()
  const navigate = useNavigate()
  const location = useLocation()

  // 자식 메뉴 active 판정 — 같은 경로를 ?mode= 로만 구분하는 항목(단편/법정)을 분리해서 강조.
  // mode 미지정 경로 진입 시 기본값은 'single' (SafetyEducation lazy init 과 일치).
  const isChildActive = (to: string, end?: boolean): boolean => {
    const qIdx = to.indexOf('?')
    const path = qIdx >= 0 ? to.slice(0, qIdx) : to
    const search = qIdx >= 0 ? to.slice(qIdx + 1) : ''
    const pathMatch = end
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(path + '/')
    if (!pathMatch) return false
    const want = new URLSearchParams(search).get('mode')
    if (!want) return true
    const cur = new URLSearchParams(location.search).get('mode') ?? 'single'
    return cur === want
  }

  // 그룹별 펼침 상태 — 기본 '접힘'으로 시작해 소규모 사업주가 기능 목록(25+개) 벽에
  // 압도되지 않게 한다. 그룹 헤더는 그대로 보이며 클릭 한 번으로 펼침. 사용자 선택은 기억.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('nav_expanded_groups')
      if (saved) return JSON.parse(saved)
    } catch { /* noop */ }
    return { 'nav.aiFeatures': false, 'nav.advancedAnalysis': false, 'nav.records': false }
  })

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      try { localStorage.setItem('nav_expanded_groups', JSON.stringify(next)) } catch { /* noop */ }
      return next
    })
  }

  // 사용량 한도 가드 — 한도 초과 시 navigate 차단 + 전역 모달 오픈.
  const guardedNav = useFeatureNav()
  const onFeatureClick = (e: React.MouseEvent, to: string) => {
    const feature = ROUTE_FEATURE_MAP[to]
    if (!feature) return  // 매핑 없는 라우트(records, billing 등) 는 그대로 통과
    guardedNav(e, feature, to)
  }

  // 서브메뉴(NavItem.children) 펼침 상태 — 기본: 위험성평가 펼침.
  const [expandedSubItems, setExpandedSubItems] = useState<Record<string, boolean>>({
    'nav.riskAssessmentGroup': true,
  })

  const toggleSubItem = (key: string) => {
    setExpandedSubItems((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const planTier = user?.plan_code ?? 'free'
  const isFreePlan = planTier === 'free'

  // 사이드바 공통 컨텐츠. mobile=true 인 드로어에서는 항상 라벨 노출(접힘 무시).
  const SidebarContent = ({ mobile = false }: { mobile?: boolean } = {}) => {
    const collapsed = mobile ? false : collapsedProp
    return (
    <div className="flex flex-col h-full">
      {/* 로고 — 접혔을 때 "Heinrich" 텍스트 숨기고 아이콘만 중앙 정렬 */}
      <div
        className={cn(
          'h-16 flex items-center border-b border-surface-elevated/40 shrink-0',
          collapsed ? 'justify-center px-0' : 'px-4',
        )}
      >
        <Link
          to="/dashboard"
          onClick={onMobileClose}
          className={cn('inline-flex items-center', !collapsed && 'flex-1')}
        >
          <Logo size="sm" showText={!collapsed} />
        </Link>
      </div>

      {/* 스크롤 영역 */}
      <nav className="flex-1 overflow-y-auto scrollbar-hide py-3 px-2">
        {/* 대시보드 (단독) */}
        <div className="mb-1">
          <NavLink
            to="/dashboard"
            end
            onClick={onMobileClose}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200',
                isActive
                  ? 'bg-white text-zinc-900 dark:bg-zinc-800 dark:text-white font-bold shadow-[inset_3px_0_0_0_rgb(217_119_6)] dark:shadow-[inset_3px_0_0_0_rgb(251_191_36)]'
                  : 'text-ink-muted hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-white/[0.04]',
                collapsed && 'justify-center px-2',
              )
            }
            title={collapsed ? t('nav.dashboard') : undefined}
          >
            <FiHome size={16} className="shrink-0" />
            {!collapsed && <span className="truncate">{t('nav.dashboard')}</span>}
          </NavLink>
        </div>

        {/* 사업장 관리 (단독) — 의무·교육·자료함 통합 진입점.
            2026-06-06: prod 노출. */}
        <div className="mb-2">
          <NavLink
            to="/dashboard/site"
            onClick={onMobileClose}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200',
                isActive
                  ? 'bg-white text-zinc-900 dark:bg-zinc-800 dark:text-white font-bold shadow-[inset_3px_0_0_0_rgb(217_119_6)] dark:shadow-[inset_3px_0_0_0_rgb(251_191_36)]'
                  : 'text-ink-muted hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-white/[0.04]',
                collapsed && 'justify-center px-2',
              )
            }
            title={collapsed ? t('nav.siteManagement') : undefined}
          >
            <FiBriefcase size={16} className="shrink-0" />
            {!collapsed && <span className="truncate">{t('nav.siteManagement')}</span>}
          </NavLink>
        </div>

        {/* Partner Safety OS — 원청(도급) 조직 멤버에게만 노출. 사업장 관리 바로 아래(테마 준수). */}
        {user?.org?.is_prime_member && (
          <div className="mb-1">
            <NavLink
              to="/dashboard/partners"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200',
                  isActive
                    ? 'bg-white text-zinc-900 dark:bg-zinc-800 dark:text-white font-bold shadow-[inset_3px_0_0_0_rgb(217_119_6)] dark:shadow-[inset_3px_0_0_0_rgb(251_191_36)]'
                    : 'text-ink-muted hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-white/[0.04]',
                  collapsed && 'justify-center px-2',
                )
              }
              title={collapsed ? t('nav.partners', { defaultValue: '협력사 관리' }) : undefined}
            >
              <FiGrid size={16} className="shrink-0" />
              {!collapsed && <span className="truncate">{t('nav.partners', { defaultValue: '협력사 관리' })}</span>}
            </NavLink>
          </div>
        )}

        {/* 메뉴 그룹 */}
        {navGroups.map((group) => (
          <div key={group.labelKey} className="mb-1">
            {/* 그룹 헤더 (접힘 상태에서는 구분선만) */}
            {!collapsed && group.collapsible && (
              <button
                onClick={() => toggleGroup(group.labelKey)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-ink-faint uppercase tracking-wider hover:text-ink-muted transition-colors"
              >
                <div className="flex items-center gap-1.5">
                  {group.icon}
                  {t(group.labelKey)}
                </div>
                <FiChevronDown
                  size={12}
                  className={cn(
                    'transition-transform duration-200',
                    expandedGroups[group.labelKey] && 'rotate-180',
                  )}
                />
              </button>
            )}
            {/* 접힘 상태에서 그룹 구분선 */}
            {collapsed && <div className="mx-2 my-2 h-px bg-surface-elevated/40" />}

            {/* 아이템 목록 */}
            <AnimatePresence initial={false}>
              {(collapsed || expandedGroups[group.labelKey]) && (
                <motion.div
                  initial={collapsed ? false : { height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  {group.items.map((item) => {
                    if (item.children && item.children.length > 0) {
                      // 서브메뉴 부모 — to 없이 펼침 토글.
                      const subOpen = expandedSubItems[item.labelKey] ?? true
                      return (
                        <div key={item.labelKey}>
                          <button
                            type="button"
                            onClick={() => toggleSubItem(item.labelKey)}
                            className={cn(
                              'w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-all duration-200',
                              'text-ink-muted hover:text-ink hover:bg-black/[0.04] dark:hover:bg-white/[0.04] font-normal',
                              collapsed ? 'justify-center px-2' : 'ml-1',
                            )}
                            title={collapsed ? t(item.labelKey) : undefined}
                          >
                            <span className="shrink-0">{item.icon}</span>
                            {!collapsed && (
                              <>
                                <span className="truncate flex-1 text-left">{t(item.labelKey)}</span>
                                <FiChevronDown
                                  size={12}
                                  className={cn('transition-transform', subOpen && 'rotate-180')}
                                />
                              </>
                            )}
                          </button>
                          <AnimatePresence initial={false}>
                            {(collapsed || subOpen) && (
                              <motion.div
                                initial={collapsed ? false : { height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.18 }}
                                className="overflow-hidden"
                              >
                                {item.children.map((child) => (
                                  <NavLink
                                    key={child.to}
                                    to={child.to!}
                                    end={child.end}
                                    onClick={(e) => {
                                      onFeatureClick(e, child.to!)
                                      if (!e.defaultPrevented) onMobileClose?.()
                                    }}
                                    className={
                                      cn(
                                        'flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-all duration-200',
                                        isChildActive(child.to!, child.end)
                                          ? 'bg-white text-zinc-900 dark:bg-zinc-800 dark:text-white font-bold shadow-[inset_3px_0_0_0_rgb(217_119_6)] dark:shadow-[inset_3px_0_0_0_rgb(251_191_36)]'
                                          : 'text-ink-muted hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-white/[0.04] font-normal',
                                        collapsed ? 'justify-center px-2' : 'ml-6',
                                      )
                                    }
                                    title={collapsed ? t(child.labelKey) : undefined}
                                  >
                                    <span className="shrink-0">{child.icon}</span>
                                    {!collapsed && <span className="truncate">{t(child.labelKey)}</span>}
                                  </NavLink>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )
                    }
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to!}
                        onClick={(e) => {
                          onFeatureClick(e, item.to!)
                          if (!e.defaultPrevented) onMobileClose?.()
                        }}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-all duration-200',
                            isActive
                              ? 'bg-white text-zinc-900 dark:bg-zinc-800 dark:text-white font-bold shadow-[inset_3px_0_0_0_rgb(217_119_6)] dark:shadow-[inset_3px_0_0_0_rgb(251_191_36)]'
                              : 'text-ink-muted hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-white/[0.04] font-normal',
                            collapsed ? 'justify-center px-2' : 'ml-1',
                          )
                        }
                        title={collapsed ? t(item.labelKey) : undefined}
                      >
                        <span className="shrink-0">{item.icon}</span>
                        {!collapsed && <span className="truncate">{t(item.labelKey)}</span>}
                      </NavLink>
                    )
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </nav>

      {/* 하단: 플랜 + 사용량 mini-bar */}
      <div className="border-t border-surface-elevated/40 p-3 flex flex-col gap-1">
        {/* 청구/설정 */}
        {[
          { to: '/dashboard/billing', labelKey: 'nav.billing', icon: <FiCreditCard size={16} /> },
          { to: '/dashboard/settings', labelKey: 'nav.settings', icon: <FiSettings size={16} /> },
        ].map(({ to, labelKey, icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onMobileClose}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200',
                isActive
                  ? 'bg-white text-zinc-900 dark:bg-zinc-800 dark:text-white font-bold shadow-[inset_3px_0_0_0_rgb(217_119_6)] dark:shadow-[inset_3px_0_0_0_rgb(251_191_36)]'
                  : 'text-ink-muted hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-white/[0.04]',
                collapsed && 'justify-center px-2',
              )
            }
            title={collapsed ? t(labelKey) : undefined}
          >
            <span className="shrink-0">{icon}</span>
            {!collapsed && <span className="truncate">{t(labelKey)}</span>}
          </NavLink>
        ))}

        {/* 플랜 뱃지 + 사용량 (펼침 상태에서만) */}
        {!collapsed && (
          <div className="mt-2 p-3 rounded-md bg-surface-elevated/30 border border-surface-elevated/40">
            <div className="flex items-center justify-between mb-2">
              <span
                className={cn(
                  'text-xs font-semibold px-2 py-0.5 rounded-full',
                  planBadgeStyle[planTier] ?? planBadgeStyle.free,
                )}
              >
                {t(PLAN_LABEL_KEYS[planTier] ?? 'common.plan.free', { defaultValue: planTier })}
              </span>
              {isFreePlan && (
                <button
                  onClick={() => { navigate('/dashboard/billing'); onMobileClose?.() }}
                  className="text-xs text-brand-primary-light hover:text-brand-primary font-medium transition-colors"
                >
                  {t('nav.upgradeShort', { defaultValue: '업그레이드' })}
                </button>
              )}
            </div>
            {/* 주요 사용량 미니바 — 모든 사용자 노출 */}
            {usage && (
              <div className="flex flex-col gap-1.5">
                <ProgressBar
                  label={t('nav.jsaShort')}
                  value={usage.jsaGenerated}
                  max={planTier === 'free' ? 5 : planTier === 'starter' ? 100 : -1}
                  size="xs"
                  showText={false}
                />
                <ProgressBar
                  label={t('nav.chatbot')}
                  value={usage.chatMessages}
                  max={planTier === 'free' ? 100 : planTier === 'starter' ? 1000 : -1}
                  size="xs"
                  showText={false}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    )
  }

  return (
    <>
      {/* 데스크톱 사이드바 */}
      <aside
        className={cn(
          'relative hidden md:flex flex-col h-full bg-surface border-r border-surface-elevated/40',
          'transition-all duration-300',
          collapsedProp ? 'w-16' : 'w-60',
        )}
      >
        <SidebarContent />
        {/* 토글 버튼 */}
        <button
          onClick={onToggle}
          className={cn(
            'absolute -right-3 top-20 z-10',
            'h-6 w-6 rounded-full bg-surface-card border border-surface-elevated/60',
            'flex items-center justify-center text-ink-faint hover:text-ink',
            'transition-colors shadow-card-dark',
          )}
          aria-label={collapsedProp ? t('nav.expandSidebar', { defaultValue: '사이드바 펼치기' }) : t('nav.collapseSidebar', { defaultValue: '사이드바 접기' })}
        >
          {collapsedProp ? <FiChevronRight size={12} /> : <FiChevronLeft size={12} />}
        </button>
      </aside>

      {/* 모바일 드로어 오버레이 */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* 배경 dim */}
            <motion.div
              className="fixed inset-0 z-40 bg-black/60 md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onMobileClose}
            />
            {/* 드로어 — 모바일에서는 항상 라벨 노출 (w-72 로 넉넉하게) */}
            <motion.aside
              className="fixed left-0 top-0 bottom-0 z-50 w-72 max-w-[85vw] bg-surface border-r border-surface-elevated/40 md:hidden flex flex-col shadow-md"
              initial={{ x: -288 }}
              animate={{ x: 0 }}
              exit={{ x: -288 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              <SidebarContent mobile />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
